import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown, Star, MapPin } from "lucide-react";
import { IMAGES, WA_DEFAULT } from "@/lib/data";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: yBg }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Trattamento di massoterapia professionale in uno studio rilassante"
          className="w-full h-full object-cover scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF8F5] via-transparent to-white/40" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 w-full pt-32 pb-24">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease: EASE }}
          className="text-xs uppercase tracking-[0.35em] font-semibold text-[#489A88] mb-8"
          data-testid="hero-overline"
        >
          Francesca Cicardi · Massoterapista M.C.B.
        </motion.p>

        <h1 className="font-light text-[#1A2E2A] tracking-tight leading-[1.02] text-[clamp(2.75rem,7.5vw,5.75rem)] max-w-4xl" data-testid="hero-title">
          <MaskedLine delay={0.45}>Massoterapia</MaskedLine>
          <MaskedLine delay={0.58}>
            <span className="italic text-[#489A88]">Professionale</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
          className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#4A5568]"
          data-testid="hero-subtitle"
        >
          Trattamenti mirati e massaggi terapeutici personalizzati per eliminare il dolore muscolare,
          sciogliere le tensioni e ritrovare il tuo benessere quotidiano.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-whatsapp-cta"
            className="group inline-flex items-center gap-3 rounded-full bg-[#489A88] text-white font-medium px-8 py-4 text-base shadow-[0_12px_32px_-8px_rgba(72,154,136,0.55)] hover:bg-[#2D6D60] hover:shadow-[0_16px_40px_-8px_rgba(45,109,96,0.5)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5"
          >
            <MessageCircle className="w-5 h-5 transition-transform duration-300 group-hover:rotate-12" />
            Prenota su WhatsApp
          </a>
          <a
            href="#trattamenti"
            data-testid="hero-discover-cta"
            className="group inline-flex items-center gap-2 rounded-full border border-[#1A2E2A]/15 bg-white/60 backdrop-blur-sm text-[#1A2E2A] font-medium px-8 py-4 text-base hover:border-[#489A88] hover:text-[#2D6D60] transition-colors duration-300"
          >
            Scopri i trattamenti
            <ArrowDown className="w-4 h-4 transition-transform duration-300 group-hover:translate-y-1" />
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.35 }}
          style={{ opacity }}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-4"
        >
          <div className="flex items-center gap-3 rounded-2xl bg-white/70 backdrop-blur-md border border-white/60 px-5 py-3 shadow-[0_8px_30px_-12px_rgba(26,46,42,0.25)]" data-testid="hero-rating-badge">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#489A88] text-[#489A88]" />
              ))}
            </div>
            <span className="text-sm font-medium text-[#1A2E2A]">5/5 su Google · 4,9/5 su Treatwell</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#64748B]">
            <MapPin className="w-4 h-4 text-[#489A88]" />
            Via Monte Carso 19, Vanzago (MI)
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
